/**
 * NewTemplatePage — /agents/templates/new
 * Template creation on the FormPage template. Demo page: /form-layouts (sectioned form with a side card). The aside is
 * the Field Help panel (follows the focused field) with the assistant one click away (entity-management §2.3).
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useWatch } from 'react-hook-form';
import { Bot } from 'lucide-react';
import { createTemplateMutation, listTemplatesQueryKey } from '../../generated/api/@tanstack/react-query.gen.js';
import { Form, FormActions, useZodForm } from '../../components/form/index.js';
import { Button, useToast } from '../../components/ui/index.js';
import { FormPage } from '../../components/page/index.js';
import { Can } from '../../components/auth/Can.js';
import { EntityAssistantDrawer } from '../../components/assistant/EntityAssistantDrawer.js';
import { PAGE_HELP } from '../../help/index.js';
import { TEMPLATE_FIELD_HELP } from '../../help/template-fields.js';
import { toSlug } from '../../lib/slug.js';
import {
  EMPTY_TEMPLATE_VALUES,
  TemplateFields,
  formValuesToUpsert,
  templateAssistantPatch,
  templateAssistantValues,
  templateFormSchema,
  type TemplateFormValues,
} from './TemplateForm.js';
import { FieldHelpAside, useFocusedHelpField } from './entity-ui/FieldHelpAside.js';

const LIST_PATH = '/agents/templates';

const FIELD_LABELS: Record<string, string> = { department: 'Department', disableModelInvocation: 'Pure orchestrator' };

export const NewTemplatePage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { notify } = useToast();
  const save = useMutation(createTemplateMutation());
  // Stable for this visit: the assistant keeps one conversation per new-template session.
  const [draftKey] = useState(() => crypto.randomUUID());
  const { focusedField, onFocus } = useFocusedHelpField();

  const form = useZodForm(templateFormSchema, { defaultValues: EMPTY_TEMPLATE_VALUES });
  const watched = useWatch({ control: form.control });
  const title = watched.title ?? '';
  const slugTouched = form.formState.dirtyFields.slug;
  const disableModelInvocation = watched.disableModelInvocation ?? false;

  // The slug follows the title until the person types in the slug field themselves.
  useEffect(() => {
    if (!slugTouched && title) form.setValue('slug', toSlug(title));
  }, [title, slugTouched, form]);

  const applyAssistantPatch = (partial: Record<string, unknown>) => {
    form.reset({ ...form.getValues(), ...templateAssistantPatch(partial) }, { keepDefaultValues: true });
  };

  const submit = async (values: TemplateFormValues) => {
    // Create-only: the server answers 409 on `slug` when it is taken, and `<Form>` puts that under the field.
    await save.mutateAsync({ body: { slug: values.slug, ...formValuesToUpsert(values) } });
    await queryClient.invalidateQueries({ queryKey: listTemplatesQueryKey() });
    notify('Template created');
    navigate(`${LIST_PATH}/${values.slug}`);
  };

  return (
    <Can policy="CanManageAgentTemplates">
      <FormPage
        title="New template"
        subtitle="Define an agent's persona, instructions and tools. Workflow steps pick a template to shape how that step's agent works."
        breadcrumbs={[
          { label: 'Libraries', to: '/agents/libraries' },
          { label: 'Templates', to: LIST_PATH },
        ]}
        helpContent={PAGE_HELP['templates']}
        backTo={LIST_PATH}
        backLabel="Templates"
        aside={
          <FieldHelpAside
            helpMap={TEMPLATE_FIELD_HELP}
            labels={FIELD_LABELS}
            focusedField={focusedField}
            overviewTitle="Templates"
            overviewBody="Templates define an agent's persona, expertise and available tools. Start with a title and a one-line goal, then refine the instructions as you go."
            assistant={
              <div className="space-y-3">
                <p className="text-sm text-body">Not sure where to start? Ask what this template should do.</p>
                <EntityAssistantDrawer
                  subjectType="template"
                  draftKey={draftKey}
                  entryPoint="persona-editor"
                  currentFormValues={templateAssistantValues({
                    ...EMPTY_TEMPLATE_VALUES,
                    ...watched,
                  } as TemplateFormValues)}
                  fieldFocus={focusedField ?? undefined}
                  onApplyFieldUpdate={applyAssistantPatch}
                  trigger={(open) => (
                    <Button variant="secondary" size="sm" onClick={open}>
                      <Bot size={14} aria-hidden="true" />
                      Ask assistant
                    </Button>
                  )}
                />
              </div>
            }
          />
        }
      >
        <Form form={form} onSubmit={submit} onFocus={onFocus} guardUnsaved className="space-y-8">
          <TemplateFields slugEditable disableModelInvocation={disableModelInvocation} />
          <FormActions submitLabel="Create template" cancelTo={LIST_PATH} />
        </Form>
      </FormPage>
    </Can>
  );
};

export default NewTemplatePage;
